/**
 * Portal — Primeiro Acesso (Public, sem autenticação)
 *
 * Página de ativação do parceiro (dono OU colaborador). O usuário chega aqui
 * pelo link do e-mail de convite: /portal/primeiro-acesso?token=<JWT>.
 *
 * Fluxo:
 *  1. Valida o token no mount (GET /portal/auth/activate/validate) → mostra o
 *     nome/email do parceiro.
 *  2. Usuário define a senha → POST /portal/auth/activate ativa o User já criado
 *     e devolve tokens.
 *  3. Auto-login: tokens persistidos → hard redirect para /portal/inicio.
 *
 * Estética "Ethereal Green" — espelha PortalLogin.
 */
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { AlertCircle, CheckCircle, Eye, EyeOff, Loader2 } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { portalAuthApi, getErrorMessage } from "@/services/portalApi";
import { marcarDonoDoPush } from "@/lib/push/donoDoPush";
import type { PortalActivateValidateResponse } from "@/types/portal";
import { FechadaNaManutencao } from "@/modules/maintenance/components/FechadaNaManutencao";

// ============================================================================
// Validation Schema — mesma regra de força da ativação interna
// ============================================================================

const primeiroAcessoSchema = z
  .object({
    password: z
      .string()
      .min(8, "A senha deve ter no mínimo 8 caracteres")
      .max(100, "Senha muito longa")
      .regex(/[A-Z]/, "Inclua ao menos uma letra maiúscula")
      .regex(/[a-z]/, "Inclua ao menos uma letra minúscula")
      .regex(/[0-9]/, "Inclua ao menos um número"),
    password_confirmation: z.string().min(1, "Confirme a senha"),
  })
  .refine((d) => d.password === d.password_confirmation, {
    message: "As senhas não coincidem",
    path: ["password_confirmation"],
  });

type PrimeiroAcessoFormData = z.infer<typeof primeiroAcessoSchema>;

const CURRENT_YEAR = new Date().getFullYear();

// ============================================================================
// Component
// ============================================================================

const PortalPrimeiroAcessoConteudo: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [validation, setValidation] = useState<PortalActivateValidateResponse | null>(null);
  const [isValidating, setIsValidating] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [activated, setActivated] = useState(false);

  // Valida o token ao montar
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!token) {
        if (!cancelled) {
          setValidation({ valid: false, error: "Token de convite não encontrado na URL" });
          setIsValidating(false);
        }
        return;
      }
      try {
        const result = await portalAuthApi.validateActivation(token);
        if (!cancelled) setValidation(result);
      } catch (error) {
        if (!cancelled) {
          setValidation({ valid: false, error: getErrorMessage(error) });
        }
      } finally {
        if (!cancelled) setIsValidating(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PrimeiroAcessoFormData>({
    resolver: zodResolver(primeiroAcessoSchema),
    mode: "onBlur",
  });

  const onSubmit = async (data: PrimeiroAcessoFormData) => {
    if (!token) return;
    setSubmitError("");
    try {
      await portalAuthApi.activate({
        token,
        password: data.password,
        password_confirmation: data.password_confirmation,
      });
      // Auto-login: tokens já persistidos pelo portalAuthApi.activate.
      // O primeiro acesso é um login do portal que NÃO passa pelo `login` do PortalAuthContext (o hard redirect abaixo faz o
      // provider restaurar o usuário do armazenamento). Sem a marca aqui, com a marca "interno" no aparelho o parceiro novo nunca
      // tomaria o push (decisão de 12/10/2026: o push só troca de dono num clique ou num login).
      marcarDonoDoPush("portal");
      setActivated(true);
      // Hard redirect re-inicializa o PortalAuthProvider com os tokens novos.
      window.location.href = "/portal/inicio";
    } catch (error) {
      setSubmitError(getErrorMessage(error));
    }
  };

  const isLoading = isSubmitting || activated;

  return (
    <div className="bg-emerald-50/40 dark:bg-gray-950 font-body text-ep-on-surface dark:text-gray-100 h-dvh overflow-y-auto flex flex-col items-center selection:bg-emerald-100 relative">
      {/* ── Background Blobs ── */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[10%] -left-[5%] w-[40vw] h-[40vw] rounded-full blur-[120px] bg-emerald-300/35 dark:bg-emerald-500/20" />
        <div className="absolute bottom-[5%] right-[2%] w-[35vw] h-[35vw] rounded-full blur-[100px] bg-green-200/40 dark:bg-green-500/15" />
        <div className="absolute top-[40%] left-[45%] w-[25vw] h-[25vw] rounded-full blur-[90px] bg-teal-200/30 dark:bg-teal-500/10" />
      </div>

      <main className="flex-grow w-full flex items-center justify-center relative px-4 sm:px-6 z-10">
        <div className="w-full max-w-[460px]">
          <div className="bg-white/85 dark:bg-gray-900/85 backdrop-blur-[20px] rounded-xl p-6 sm:p-8 md:p-10 border border-emerald-200/40 dark:border-emerald-900/30 shadow-[0_12px_40px_rgba(16,78,50,0.08)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.4)] animate-ep-fade-in">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img
                src="/ponto-banese-logo.png"
                alt="Ponto Banese"
                className="w-20 h-20 sm:w-24 sm:h-24 object-contain drop-shadow-sm"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            </div>

            <div className="mb-6 text-center">
              <h1 className="font-headline text-2xl sm:text-3xl font-extrabold tracking-tight text-emerald-800 dark:text-emerald-300 mb-2">
                Primeiro acesso
              </h1>
              <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                Crie sua senha para ativar o acesso ao Portal do Parceiro
              </p>
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-emerald-200/60 dark:via-emerald-900/40 to-transparent mb-6" />

            {/* Estado: validando token */}
            {isValidating && (
              <div
                className="flex flex-col items-center gap-3 py-8 text-slate-500"
                role="status"
                aria-live="polite"
              >
                <Loader2 className="h-7 w-7 animate-spin text-emerald-600" />
                <span className="text-sm">Validando convite...</span>
              </div>
            )}

            {/* Estado: token inválido/expirado */}
            {!isValidating && validation && !validation.valid && (
              <div className="space-y-5">
                <Alert
                  variant="destructive"
                  className="border-red-200 bg-red-50/80 dark:bg-red-950/30"
                >
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    {validation.error || "Convite inválido ou expirado."}
                  </AlertDescription>
                </Alert>
                <Button
                  type="button"
                  className="w-full h-11 bg-gradient-to-br from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800"
                  onClick={() => {
                    window.location.href = "/portal/login";
                  }}
                >
                  Ir para o login
                </Button>
              </div>
            )}

            {/* Estado: token válido → formulário de senha */}
            {!isValidating && validation && validation.valid && (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                {validation.first_name && (
                  <p className="text-sm text-slate-600 dark:text-slate-300 text-center">
                    Olá, <strong>{validation.first_name}</strong>
                    {validation.email ? (
                      <>
                        {" "}
                        — <span className="text-emerald-700">{validation.email}</span>
                      </>
                    ) : null}
                  </p>
                )}

                {submitError && (
                  <Alert
                    variant="destructive"
                    className="border-red-200 bg-red-50/80 dark:bg-red-950/30"
                  >
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-sm">
                      {submitError}
                      {" — "}
                      <button
                        type="button"
                        className="underline font-medium hover:opacity-80"
                        onClick={() => {
                          window.location.href = "/portal/login";
                        }}
                      >
                        Ir para o login
                      </button>
                    </AlertDescription>
                  </Alert>
                )}

                {activated && (
                  <Alert className="border-emerald-200 bg-emerald-50/80 dark:bg-emerald-950/30">
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                    <AlertDescription className="text-sm">
                      Conta ativada! Entrando no portal...
                    </AlertDescription>
                  </Alert>
                )}

                {/* Senha */}
                <div className="space-y-2">
                  <Label
                    htmlFor="password"
                    className="text-sm font-medium text-slate-700 dark:text-slate-200"
                  >
                    Nova senha
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Mínimo 8 caracteres"
                      autoComplete="new-password"
                      className={`h-11 text-sm pr-10 bg-white/60 dark:bg-gray-800/60 border-emerald-200/60 dark:border-gray-700 focus-visible:ring-emerald-500 focus-visible:border-emerald-400 ${
                        errors.password ? "border-red-400 focus-visible:ring-red-400" : ""
                      }`}
                      disabled={isLoading}
                      {...register("password")}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-emerald-600 transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading}
                      aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-xs text-red-500">{errors.password.message}</p>
                  )}
                </div>

                {/* Confirmação */}
                <div className="space-y-2">
                  <Label
                    htmlFor="password_confirmation"
                    className="text-sm font-medium text-slate-700 dark:text-slate-200"
                  >
                    Confirme a senha
                  </Label>
                  <div className="relative">
                    <Input
                      id="password_confirmation"
                      type={showConfirmation ? "text" : "password"}
                      placeholder="Repita a senha"
                      autoComplete="new-password"
                      className={`h-11 text-sm pr-10 bg-white/60 dark:bg-gray-800/60 border-emerald-200/60 dark:border-gray-700 focus-visible:ring-emerald-500 focus-visible:border-emerald-400 ${
                        errors.password_confirmation
                          ? "border-red-400 focus-visible:ring-red-400"
                          : ""
                      }`}
                      disabled={isLoading}
                      {...register("password_confirmation")}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-emerald-600 transition-colors"
                      onClick={() => setShowConfirmation(!showConfirmation)}
                      disabled={isLoading}
                      aria-label={showConfirmation ? "Ocultar senha" : "Mostrar senha"}
                    >
                      {showConfirmation ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {errors.password_confirmation && (
                    <p className="text-xs text-red-500">{errors.password_confirmation.message}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-base font-semibold bg-gradient-to-br from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 shadow-md hover:shadow-lg transition-all active:scale-[0.99]"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Ativando...
                    </>
                  ) : (
                    "Ativar e entrar"
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </main>

      <footer className="w-full flex items-center justify-between px-6 sm:px-10 py-4 text-[11px] uppercase tracking-wider text-emerald-900/50 dark:text-emerald-200/40 z-10 flex-shrink-0">
        <span>© {CURRENT_YEAR} Ponto Banese · Portal do Parceiro</span>
      </footer>
    </div>
  );
};

/** Invólucro: fecha a tela e volta pro /portal/login durante a manutenção
 * (o login do portal mostra o aviso) — ver `FechadaNaManutencao`. Os hooks
 * da tela ficam todos dentro de `PortalPrimeiroAcessoConteudo`, nunca depois
 * de um return condicional. */
const PortalPrimeiroAcesso: React.FC = () => (
  <FechadaNaManutencao destino="/portal/login">
    <PortalPrimeiroAcessoConteudo />
  </FechadaNaManutencao>
);

export default PortalPrimeiroAcesso;
